import React, { useEffect, useState } from "react";
import { apiGet } from "../../API/Api_Url";
import { X, MapPin, Calendar, DollarSign, Users, Clock } from "lucide-react";

function MyCancelledServiceRequirements({ WordRequirementState, setWordRequirementState }) {
    const [Requirements, setRequirements] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);

    const GetUserAllRequirements = async () => {
        try {
            setIsUploading(true);
            const response = await apiGet("api/user/GetMyCancelledServiceRequirements");
            setRequirements(response.data);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setIsUploading(false);
        }
    };

    useEffect(() => {
        GetUserAllRequirements();
    }, []);

    const getStatusColor = (status) => {
        switch (status) {
            case "Pending":
                return "bg-yellow-100 text-yellow-800 border-yellow-200";
            case "Cancelled":
                return "bg-green-100 text-green-800 border-green-200";
            case "Rejected":
                return "bg-red-100 text-red-800 border-red-200";
            default:
                return "bg-gray-100 text-gray-800 border-gray-200";
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const ImageModal = () => {
        if (!selectedImage) return null;
        return (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
                <div className="relative max-w-4xl max-h-full">
                    <button
                        onClick={() => setSelectedImage(null)}
                        className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
                    >
                        <X size={32} />
                    </button>
                    <img
                        src={selectedImage}
                        alt="Service site"
                        className="max-w-full max-h-screen object-contain rounded-lg"
                    />
                </div>
            </div>
        );
    };

    return (
        <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-100 min-h-screen">
            <div className="max-w-5xl mx-auto">
                <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-1 md:mb-0">
                            My Cancelled Service Requirements
                        </h1>
                        <p className="text-gray-600">
                            Manage and track your Service projects
                        </p>
                    </div>

                    <div className="mt-4 md:mt-0">
                        <select
                            value={WordRequirementState}
                            onChange={(e) => setWordRequirementState(e.target.value)}
                            className="border border-gray-300 rounded-lg px-4 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0060B9]"
                        >
                            <option value="All">All</option>
                            <option value="Pending">Pending</option>
                            <option value="Approved">Approved</option>
                            <option value="InProgress">InProgress</option>
                            <option value="Completed">Completed </option>
                            <option value="Cancelled ">Cancelled  </option>
                        </select>
                    </div>
                </div>

                {isUploading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                        <p className="ml-4 text-gray-600">Loading your projects...</p>
                    </div>
                ) : Requirements?.length === 0 ? (
                    <div className="text-center py-20">
                        <p className="text-xl text-gray-500">No requirements found.</p>
                        <p className="text-gray-400 mt-2">
                            Create your first Service project to get started.
                        </p>
                    </div>
                ) : (
                    <div className="grid gap-6 grid-cols-1">
                        {Requirements.map((req) => (
                            <div
                                key={req?._id}
                                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                            >
                                <div className="p-6">
                                    <div className="flex justify-between items-start mb-4">
                                        <h3 className="text-xl font-bold text-gray-900">
                                            {req?.ServiceType}
                                        </h3>
                                        <span
                                            className={`px-3 py-1 text-xs font-semibold rounded-full border ${getStatusColor(
                                                req?.Status
                                            )}`}
                                        >
                                            {req?.Status}
                                        </span>
                                    </div>

                                    <div className="flex items-start text-gray-600 mb-4">
                                        <MapPin className="flex-shrink-0 h-4 w-4 mr-2 mt-0.5" />
                                        <a
                                            href={`https://www.google.com/maps?q=${req?.Location?.Late},${req?.Location?.Long}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:underline"
                                        >
                                            {req?.Location?.Address}, {req?.City}, {req?.State} - {req?.Pincode}
                                        </a>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div className="flex items-center text-gray-700">
                                            <Users className="h-4 w-4 mr-2 text-green-500" />
                                            <div>
                                                <p className="font-medium">When do you need the service?</p>
                                                <p className="text-gray-600">{req?.Whendoyouneedtheservice}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center text-gray-700">
                                            <DollarSign className="h-4 w-4 mr-2 text-purple-500" />
                                            <div>
                                                <p className="font-medium">Budget</p>
                                                <p className="text-gray-600">{req?.Budget}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center text-gray-700">
                                            <Calendar className="h-4 w-4 mr-2 text-blue-500" />
                                            <div>
                                                <p className="font-medium">Payment Method</p>
                                                <p className="text-gray-600">{req?.PaymentMethod}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="px-6 py-4 bg-gray-50 border-t flex justify-between items-center text-sm text-gray-500">
                                    <div className="flex items-center">
                                        <Calendar className="h-4 w-4 mr-1" />
                                        <span>Created: {formatDate(req?.createdAt)}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <ImageModal />
        </div>
    );
}

export default MyCancelledServiceRequirements;
